import {
  GridApi,
  type GridReadyEvent,
  ModuleRegistry,
  type RowClassParams,
  type RowHeightParams,
} from 'ag-grid-community'
import { AllEnterpriseModule } from 'ag-grid-enterprise'
import { ReactNode, useCallback, useMemo } from 'react'

import { CheckboxWithLabel } from '@/components/lemmala-design-system/checkbox/checkbox'
import { AgDataTable } from '@/components/lemmala-design-system/table'
import {
  TABLE_HEADER_HEIGHTS,
  TABLE_ROW_HEIGHTS,
} from '@/components/lemmala-design-system/table/consts'
import { usePersistedGrouped } from '@/components/lemmala-design-system/table/hooks/use-persisted-grouped'
import { TABLE_LABELS } from '@/components/lemmala-design-system/table/labels'
import type {
  RowClickMeta,
  RowGroupColumnConfig,
  RowGroupConfig,
  RowGroupFilterConfig,
  RowGroupGridOptions,
  RowGroupSearchConfig,
} from '@/components/lemmala-design-system/table/types'

import { AgDataTableProps } from './ag-data-table'
import { DefaultGroupRowRenderer } from './components/default-group-row-renderer'

ModuleRegistry.registerModules([AllEnterpriseModule])

export interface AgRowGroupTableProps<
  TData extends Record<string, unknown>,
  TRow extends TData = TData,
> {
  data: TData[]
  isLoading?: boolean
  tableHeaderActions?: ReactNode
  onRowClick?: (row: TRow, meta: RowClickMeta) => void
  filterContext?: string
  rowGroupConfig: RowGroupConfig<TData, TRow>
  columnConfig: RowGroupColumnConfig<TRow>
  filterConfig: RowGroupFilterConfig<TRow>
  searchConfig?: RowGroupSearchConfig<TRow>
  gridOptionsConfig?: RowGroupGridOptions<TRow>
  className?: string
  tableClassName?: string
  suppressColumnHeaders?: boolean
  customFilterBarLeft?: ReactNode
  onGridInit?: (event: GridReadyEvent<TRow>) => void
  onGridReady?: (api: GridApi<TRow>) => void
  urlSync?: AgDataTableProps<TRow>['urlSync']
  additionalTableProps?: Partial<
    Omit<
      AgDataTableProps<TRow>,
      | 'rowData'
      | 'columnDefs'
      | 'gridOptions'
      | 'onRowClick'
      | 'onGridReady'
      | 'onFilterChanged'
      | 'search'
      | 'filters'
      | 'customFilterBarLeft'
      | 'urlSync'
      | 'className'
      | 'tableClassName'
      | 'containerClassName'
    >
  >
  containerClassName?: string
}

export const AgRowGroupTable = <TData extends Record<string, unknown>, TRow extends TData = TData>({
  data,
  isLoading = false,
  tableHeaderActions,
  onRowClick,
  onGridReady,
  filterContext = 'table',
  rowGroupConfig,
  columnConfig,
  filterConfig,
  searchConfig,
  gridOptionsConfig,
  className,
  tableClassName = '',
  suppressColumnHeaders = false,
  customFilterBarLeft,
  onGridInit,
  urlSync: urlSyncConfig,
  additionalTableProps,
  containerClassName,
}: AgRowGroupTableProps<TData, TRow>) => {
  const shouldEnableGrouping = rowGroupConfig.shouldEnableGrouping?.() ?? true
  const assignGroupFields = rowGroupConfig.assignGroupFields
  const prefix = urlSyncConfig?.prefix ?? filterContext
  const [groupingEnabled, setGroupingEnabled] = usePersistedGrouped({
    prefix,
    urlParamKey: rowGroupConfig.urlParamKey,
    defaultGrouped: rowGroupConfig.defaultGrouped ?? true,
    enabled: shouldEnableGrouping && !!rowGroupConfig.groupToggleLabel,
  })

  const { hasGroups: dataHasGroups, groupedData } = useMemo(
    () => assignGroupFields(data),
    [data, assignGroupFields],
  )

  const hasGroups = useMemo(() => {
    if (!shouldEnableGrouping) {
      return false
    }
    return groupingEnabled && dataHasGroups
  }, [groupingEnabled, dataHasGroups, shouldEnableGrouping])

  const getColumnDefs = columnConfig.getColumnDefs
  const columnDefs = useMemo(
    () => getColumnDefs(hasGroups, groupedData),
    [getColumnDefs, hasGroups, groupedData],
  )

  const groupRowRenderer = useMemo(() => {
    return gridOptionsConfig?.groupRowRenderer ?? DefaultGroupRowRenderer
  }, [gridOptionsConfig?.groupRowRenderer])

  const gridOptions = useMemo(() => {
    const regularRowHeight = gridOptionsConfig?.regularRowHeight ?? TABLE_ROW_HEIGHTS.double

    const baseOptions: import('ag-grid-community').GridOptions<TRow> = {
      headerHeight: suppressColumnHeaders ? 0 : undefined,
      suppressGroupRowsSticky: true,
      getRowHeight: (params: RowHeightParams<TRow>) => {
        if (params.node.group) {
          return TABLE_HEADER_HEIGHTS.group
        }
        return regularRowHeight
      },
      getRowClass: (params: RowClassParams<TRow>) => {
        if (params.node.group) {
          return (
            gridOptionsConfig?.getGroupRowClass?.({ data: params.data }) ??
            'bg-main-background-muted'
          )
        }
        return gridOptionsConfig?.getRegularRowClass?.({ data: params.data }) ?? ''
      },
      ...(hasGroups
        ? {
            groupRowRenderer,
            groupDisplayType: 'groupRows' as const,
            groupDefaultExpanded: -1,
          }
        : {}),
      ...gridOptionsConfig?.additionalGridOptions,
    }

    return baseOptions
  }, [hasGroups, groupRowRenderer, suppressColumnHeaders, gridOptionsConfig])

  const handleRowClick = useCallback(
    (row: TRow, meta: RowClickMeta) => {
      onRowClick?.(row, meta)
    },
    [onRowClick],
  )

  if (isLoading) {
    return (
      <div className='flex h-64 items-center justify-center'>
        <div className='text-main-foreground-tertiary'>{TABLE_LABELS.loading}</div>
      </div>
    )
  }

  const groupToggle =
    rowGroupConfig.groupToggleLabel && shouldEnableGrouping ? (
      <CheckboxWithLabel
        checked={groupingEnabled}
        onCheckedChange={(checked) => setGroupingEnabled(!!checked)}
      >
        {rowGroupConfig.groupToggleLabel}
      </CheckboxWithLabel>
    ) : null

  return (
    <AgDataTable<TRow>
      rowData={groupedData}
      domLayout='normal'
      columnDefs={columnDefs}
      gridOptions={gridOptions}
      className={`ag-row-group-table ${suppressColumnHeaders ? 'ag-group-no-header' : ''} ${className || ''}`}
      tableClassName={tableClassName}
      search={searchConfig ?? undefined}
      urlSync={
        urlSyncConfig ?? {
          enabled: true,
          prefix: filterContext,
        }
      }
      onFilterChanged={filterConfig.onFilterChanged}
      onRowClick={handleRowClick}
      onGridInit={onGridInit}
      onGridReady={onGridReady}
      filters={{
        defaultVisibleFilters: filterConfig.defaultVisibleFilters,
      }}
      customFilterBarLeft={
        groupToggle || customFilterBarLeft ? (
          <>
            {groupToggle}
            {customFilterBarLeft}
          </>
        ) : undefined
      }
      headerRight={tableHeaderActions}
      containerClassName={containerClassName}
      {...additionalTableProps}
    />
  )
}
